import * as React from 'react';
import { Box, Flex, Switch, Text, VStack } from '@chakra-ui/react';
import { SegmentGroup, ISegmentItem } from '../../../shared/SegmentGroup';
import { ALL_FONTS, getFontByFamily, IFontOption } from '../config/fonts';
import {
  IScoreboardDisplayConfig,
  IScoreboardTheme,
  TitleFontSize,
} from '../types';

const FONT_SIZE_ITEMS: ISegmentItem[] = [
  { value: 'small', label: 'S' },
  { value: 'medium', label: 'M' },
  { value: 'large', label: 'L' },
  { value: 'xlarge', label: 'XL' },
];

interface DisplaySectionProps {
  theme: IScoreboardTheme;
  display: IScoreboardDisplayConfig;
  onUpdateTheme: (updates: Partial<IScoreboardTheme>) => void;
  onUpdateDisplay: (updates: Partial<IScoreboardDisplayConfig>) => void;
}

export const DisplaySection = ({
  theme,
  display,
  onUpdateTheme,
  onUpdateDisplay,
}: DisplaySectionProps): React.ReactElement => {
  const handleFontChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const font = ALL_FONTS.find(
      (f: IFontOption): boolean => f.value === e.target.value
    );
    onUpdateTheme({ fontFamily: font?.family || undefined });
  };

  const fontValue = ((): string => {
    const font = getFontByFamily(theme.fontFamily || '');
    return font ? font.value : '';
  })();

  return (
    <Box role='group' aria-labelledby='display-section-label'>
      <Text
        id='display-section-label'
        fontSize='xs'
        fontWeight={700}
        textTransform='uppercase'
        letterSpacing='wider'
        color='var(--scoreboard-text-secondary, #b0b0b0)'
        mb={3}
      >
        Display
      </Text>
      <VStack gap={3} align='stretch'>
        <Flex align='center' justify='space-between'>
          <Text
            id='font-family-label'
            fontSize='sm'
            color='var(--scoreboard-text-secondary, #b0b0b0)'
          >
            Font Family
          </Text>
          <select
            value={fontValue}
            onChange={handleFontChange}
            aria-labelledby='font-family-label'
            style={{
              width: '200px',
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.05)',
              color: 'var(--scoreboard-text-primary, #ffffff)',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            {ALL_FONTS.map(
              (font: IFontOption): React.ReactElement => (
                <option
                  key={font.value}
                  value={font.value}
                  style={{ background: '#1a1a1a' }}
                >
                  {font.label}
                </option>
              )
            )}
          </select>
        </Flex>
        <Box>
          <Text
            id='title-font-size-label'
            fontSize='sm'
            color='var(--scoreboard-text-secondary, #b0b0b0)'
            mb={1}
          >
            Title Font Size
          </Text>
          <SegmentGroup
            value={theme.titleFontSize ?? 'medium'}
            items={FONT_SIZE_ITEMS}
            onValueChange={(details: { value: string }): void =>
              onUpdateTheme({ titleFontSize: details.value as TitleFontSize })
            }
            size='sm'
            variant='fullWidth'
            aria-labelledby='title-font-size-label'
          />
        </Box>
        <Flex align='center' justify='space-between'>
          <Text fontSize='sm' color='var(--scoreboard-text-secondary, #b0b0b0)'>
            Auto-Scroll
          </Text>
          <Switch.Root
            checked={display.autoScroll}
            onCheckedChange={(details: { checked: boolean }): void =>
              onUpdateDisplay({ autoScroll: details.checked })
            }
            aria-label='Enable auto-scroll'
          >
            <Switch.HiddenInput />
            <Switch.Control
              bg='rgba(255, 255, 255, 0.1)'
              _checked={{ bg: 'var(--scoreboard-primary, #1a73e8)' }}
            >
              <Switch.Thumb bg='white' />
            </Switch.Control>
          </Switch.Root>
        </Flex>
        <Flex align='center' justify='space-between'>
          <Text fontSize='sm' color='var(--scoreboard-text-secondary, #b0b0b0)'>
            Confetti on New #1
          </Text>
          <Switch.Root
            checked={display.enableConfetti}
            onCheckedChange={(details: { checked: boolean }): void =>
              onUpdateDisplay({ enableConfetti: details.checked })
            }
            aria-label='Enable confetti animation on new #1'
          >
            <Switch.HiddenInput />
            <Switch.Control
              bg='rgba(255, 255, 255, 0.1)'
              _checked={{ bg: 'var(--scoreboard-primary, #1a73e8)' }}
            >
              <Switch.Thumb bg='white' />
            </Switch.Control>
          </Switch.Root>
        </Flex>
      </VStack>
    </Box>
  );
};
